import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const MONTHS = { monthly: 1, 3: 3, 6: 6, 12: 12, 24: 24 };

export default function BillingCarousel({ items, value, labels, shortUnit, onChange }) {
  const n = items.length;
  const idx = Math.max(0, items.findIndex((b) => b.id === value));
  const [gap, setGap] = useState(150);
  useEffect(() => {
    const f = () => setGap(window.innerWidth < 640 ? 92 : window.innerWidth < 1024 ? 124 : 150);
    f();
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);

  const go = (d) => onChange(items[((idx + d) % n + n) % n].id);
  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
    if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
  };

  return (
    <div
      data-testid="pricing-billing-toggle"
      role="radiogroup"
      tabIndex={0}
      onKeyDown={onKey}
      className="relative h-40 sm:h-44 w-full overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/60 rounded-3xl"
    >
      <div
        aria-hidden="true"
        className="absolute inset-y-5 left-1/2 -translate-x-1/2 w-[184px] sm:w-[220px] rounded-3xl bg-brand-teal/10 dark:bg-brand-teal/15 ring-1 ring-brand-teal/30"
      />
      {items.map((b, i) => {
        // circular signed offset so the carousel wraps infinitely
        let off = i - idx;
        if (off > n / 2) off -= n;
        if (off < -n / 2) off += n;
        const abs = Math.abs(off);
        const active = off === 0;
        const months = MONTHS[b.id] || 1;
        const hidden = abs > 2;
        return (
          <motion.button
            key={b.id}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={b.testid}
            data-active={active ? "true" : "false"}
            onClick={() => (active ? null : onChange(b.id))}
            initial={false}
            animate={{
              x: off * gap,
              rotateY: off * -34,
              scale: active ? 1 : 0.76 - (abs - 1) * 0.08,
              opacity: hidden ? 0 : active ? 1 : 0.72 - (abs - 1) * 0.28,
            }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            style={{
              transformPerspective: 1100,
              pointerEvents: hidden ? "none" : "auto",
              left: "50%",
              top: "50%",
              marginLeft: -88,
              marginTop: -56,
              zIndex: 10 - abs,
            }}
            className={`absolute w-[176px] h-[112px] rounded-2xl flex flex-col items-center justify-center text-center px-3 transition-colors duration-300 ${
              active
                ? "bg-brand-navy text-white shadow-2xl shadow-brand-navy/40 dark:bg-white dark:text-slate-900"
                : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:text-brand-teal cursor-pointer"
            }`}
          >
            <span className={`font-display font-extrabold tracking-tight leading-none ${active ? "text-4xl" : "text-3xl"}`}>{months}</span>
            <span className={`mt-1.5 font-semibold leading-snug ${active ? "text-[11px] text-brand-yellow dark:text-brand-tealDark" : "text-xs"}`}>
              {active ? labels[b.id] : shortUnit}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
}
